import { useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { ZONA_HORARIA, centavos, type Role } from '@abastece/shared';
import type { Abono, Cliente, Compra, Devolucion, JournalEntry, Pedido, Salida, SesionCaja, Venta } from '@abastece/core';
import { db, ordenReplay } from '../db/dexie.js';
import { leerSinEfecto, tuvoEfecto } from '../lib/sinEfecto.js';
import { readSettings } from '../db/reader.js';
import { listaPredeterminada } from '../lib/catalogo.js';
import { useMonedaDeLosLibros } from '../lib/libros.js';
import {
  DIAS_CONTROL,
  busquedaDeControl,
  controlPorPersona,
  type Control as Resultado,
  type DiasControl,
  type EventoLog,
  type Faltante,
  type FilaPersona,
  type Motivo,
} from '../lib/control.js';
import { usePuede } from '../lib/sesion.js';
import { localeActual, useT, type Clave } from '../i18n/index.js';
import { etiquetaRol } from '../i18n/dominio.js';
import { ExplicarFaltante } from '../ui/Correcciones.js';
import { FilaMoneda } from '../ui/ElegirMoneda.js';
import { Chip, Ico } from '../ui/Iconos.js';
import { Pagina, Vacio } from '../ui/Pagina.js';

/** What the report reads from the log. */
const TIPOS = [
  'venta.registrada',
  // On nobody's card, but on the page of whoever opened the till (2026-10-23).
  'caja.abierta',
  'venta.anulada',
  'devolucion.registrada',
  'inventario.salida',
  'caja.cerrada',
  'caja.entregada',
  'efectivo.recibido',
  'sesion.pin_incorrecto',
  'compra.costo_corregido',
  'abono.anulado',
  'sesion.iniciada',
  // Who was using each device when: its registration, then its sign-ins, whatever the period.
  'dispositivo.registrado',
];

const MOTIVO: Record<Motivo, Clave> = {
  lista: 'control.motivo.lista',
  rapidas: 'control.motivo.rapidas',
  margen: 'control.motivo.margen',
  fiado: 'control.motivo.fiado',
  noEsDueno: 'control.motivo.noEsDueno',
  otroNombre: 'control.motivo.otroNombre',
};

/**
 * The report over the last `dias` days, from the local log — which holds every device's events
 * once they have synced, so it works with no signal. Undefined while it is read, and for somebody
 * it is not for (`activo` false), who reads nothing.
 */
export function useControl(dias: number, activo = true): Resultado | undefined {
  return useLiveQuery(async () => {
    if (!activo) return undefined;
    const desde = new Date(Date.now() - dias * 86_400_000).toISOString();
    // What the books took: a sale voided twice is one void, and a refused handover moved no drawer.
    const sinEfecto = await leerSinEfecto();
    const todos = (await db.eventos.where('type').anyOf(TIPOS).toArray()).filter((e) => tuvoEfecto(e, sinEfecto)).sort(ordenReplay);
    const eventos: EventoLog[] = todos.filter((e) => e.clientTs >= desde);
    const entregas: EventoLog[] = todos.filter((e) => e.type === 'caja.entregada');
    const entradas: EventoLog[] = todos.filter((e) => e.type === 'sesion.iniciada' || e.type === 'dispositivo.registrado');
    const campo = (tipos: string[], nombre: string) => [
      ...new Set(
        eventos
          .filter((e) => tipos.includes(e.type))
          .map((e) => (e.payload as Record<string, unknown>)[nombre])
          .filter((v): v is string => typeof v === 'string'),
      ),
    ];
    const deCaja = eventos.filter((e) =>
      ['caja.abierta', 'caja.cerrada', 'efectivo.recibido', 'caja.entregada', 'compra.costo_corregido'].includes(e.type),
    );
    const porId = <T extends { id: string }>(filas: (T | undefined)[]) =>
      new Map(filas.filter((f): f is T => f !== undefined).map((f) => [f.id, f]));
    const compras = porId<Compra>(await db.compras.bulkGet(campo(['compra.costo_corregido'], 'compraId')));

    const [ventas, enPie, sesiones, asientos, devoluciones, salidas, clientes, usuarios, listas, config, abonos, explicaciones, pedidos] = await Promise.all([
      db.ventas.bulkGet(campo(['venta.anulada'], 'ventaId')),
      db.ventas.bulkGet(campo(['venta.registrada'], 'ventaId')),
      db.sesiones.bulkGet([
        ...new Set([
          ...campo(['caja.cerrada', 'efectivo.recibido'], 'sesionId'),
          ...entregas.map((e) => (e.payload as { sesionId: string }).sesionId),
          ...[...compras.values()].flatMap((c) => (c.sesionId ? [c.sesionId] : [])),
        ]),
      ]),
      db.asientos.where('eventId').anyOf(deCaja.map((e) => e.id)).toArray(),
      db.devoluciones.bulkGet(campo(['devolucion.registrada'], 'devolucionId')),
      db.salidas.bulkGet(campo(['inventario.salida'], 'salidaId')),
      db.clientes.bulkGet(campo(['venta.registrada'], 'clienteId')),
      db.usuarios.toArray(),
      db.listas.toArray(),
      readSettings(),
      db.abonos.bulkGet(campo(['abono.anulado'], 'abonoId')),
      db.gastos.filter((g) => g.faltanteDe != null && !g.anulado).toArray(),
      db.pedidos.bulkGet(campo(['venta.registrada'], 'pedidoId')),
    ]);
    const explicados = new Map<string, number>();
    for (const g of explicaciones) explicados.set(g.faltanteDe!, (explicados.get(g.faltanteDe!) ?? 0) + g.montoCordobas);

    return controlPorPersona({
      eventos,
      entregas,
      entradas,
      abonos: porId<Abono>(abonos),
      ventas: porId<Venta>(ventas),
      enPie: new Set(enPie.flatMap((v) => (v ? [v.id] : []))),
      sesiones: porId<SesionCaja>(sesiones),
      asientos: porEvento(asientos as JournalEntry[]),
      devoluciones: porId<Devolucion>(devoluciones),
      salidas: porId<Salida>(salidas),
      compras,
      explicados,
      corregidos: config.conteosCorregidos,
      clientes: porId<Cliente>(clientes),
      pedidos: porId<Pedido>(pedidos),
      usuarios,
      listaPredeterminadaId: listaPredeterminada(listas)?.id ?? null,
      ventaRapidaMaxima: config.ventaRapidaMaxima,
      margenMinimoBp: config.precioMargenMinimoBp,
    });
  }, [dias, activo]);
}

/**
 * Every entry of each event, in the order written. Keyed one per event, a corte that hands the
 * money over kept only its handover and lost the count's shortage, on the person's card and in
 * the alerts alike.
 */
function porEvento(asientos: readonly JournalEntry[]): Map<string, JournalEntry[]> {
  const out = new Map<string, JournalEntry[]>();
  for (const a of asientos) out.set(a.eventId, [...(out.get(a.eventId) ?? []), a]);
  return out;
}

/**
 * «Control»: what each person did with the shop's money, side by side, for the owner.
 *
 * Nothing here is an accusation — a void is a normal thing to do, and so is a price brought down
 * for a regular. What the owner looks for is the person doing much more of it than the others,
 * and the few sales that went through without the owner's yes where the counter would have asked.
 */
export function Control() {
  const t = useT();
  const navigate = useNavigate();
  // In the address, so «Volver» from a person's page finds the days it was left on.
  const busqueda = busquedaDeControl(useSearch({ from: '/control' }));
  const dias: DiasControl = busqueda.dias ?? 7;
  const elegirDias = (d: DiasControl) =>
    void navigate({ to: '/control', search: busquedaDeControl({ dias: d }), replace: true, viewTransition: false });
  const control = useControl(dias);
  /** A shortage being explained as what it went on, which only the owner does. */
  const puedeExplicar = usePuede('configurar');
  const [explicando, setExplicando] = useState<Faltante | null>(null);
  const usuarios = useLiveQuery(() => db.usuarios.toArray(), [], []);
  const dispositivos = useLiveQuery(() => db.dispositivos.toArray(), [], []);
  const config = useLiveQuery(() => readSettings(), [], null);
  const persona = (id: string) => usuarios.find((u) => u.id === id);
  const nombre = (id: string) => persona(id)?.nombre ?? t('ventas.alguien');
  const cuando = (iso: string) =>
    new Date(iso).toLocaleDateString(localeActual(), { day: 'numeric', month: 'short', timeZone: ZONA_HORARIA });
  const cuandoConHora = (iso: string) =>
    new Date(iso).toLocaleString(localeActual(), {
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: ZONA_HORARIA,
    });
  const equipo = (id: string) => {
    const d = dispositivos.find((x) => x.id === id);
    return d ? `${d.nombre} (${d.codigo})` : id.slice(0, 8);
  };
  const variosDuenos = usuarios.filter((u) => u.rol === 'dueno').length > 1;
  /**
   * Córdobas or dollars (2026-10-13), the phone's one choice for the owner's figures, as
   * «Contabilidad» reads them. A shortage explained is typed in córdobas whatever the page is in.
   */
  const libros = useMonedaDeLosLibros();
  const dinero = (c: number) => libros.dinero(centavos(c));
  // By what the count found: one explained in part stays, saying how much is explained.
  const faltantes = (control?.faltantes ?? []).filter((f) => f.monto + f.explicado >= (config?.faltanteAviso ?? 0));

  return (
    <Pagina titulo={t('control.titulo')} subtitulo={t('control.subtitulo')}>
      <ElegirDias dias={dias} onElegir={elegirDias} />
      <FilaMoneda moneda={libros.moneda} onElegir={libros.elegir} tasa={libros.tasa} className="-mt-1 mb-4" />

      {!control ? null : control.personas.length === 0 && control.entradasDueno.length === 0 ? (
        <Vacio mensaje={t('control.nada')} />
      ) : (
        <div className="flex flex-col gap-4">
          {control.sinAutorizar.length > 0 && (
            <section className="tarjeta ring-rose-500/30">
              <header className="mb-2 flex items-center gap-3">
                <Chip icono="aviso" tinte="rosa" />
                <div className="min-w-0 flex-1">
                  <h2 className="font-semibold">{t('control.sinAutorizar')}</h2>
                  <p className="text-xs text-slate-500">{t('control.sinAutorizarAyuda')}</p>
                </div>
              </header>
              <ul className="flex flex-col divide-y divide-slate-800">
                {control.sinAutorizar.map((s) => (
                  <li key={s.ventaId} className="py-2 text-sm">
                    <p className="flex justify-between gap-3">
                      <span className="tabular font-medium">{s.ticket}</span>
                      <span className="shrink-0 text-slate-400">
                        {nombre(s.vendedorId)} · {s.fecha}
                      </span>
                    </p>
                    <p className="text-xs text-rose-300">{s.motivos.map((m) => t(MOTIVO[m])).join(' · ')}</p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {faltantes.length > 0 && (
            <section className="tarjeta ring-amber-500/30">
              <header className="mb-2 flex items-center gap-3">
                <Chip icono="caja" tinte="ambar" />
                <div className="min-w-0 flex-1">
                  <h2 className="font-semibold">{t('control.faltantes')}</h2>
                  <p className="text-xs text-slate-500">{t('control.faltantesAyuda')}</p>
                </div>
              </header>
              <ul className="flex flex-col divide-y divide-slate-800">
                {faltantes.map((f) => (
                  <li key={f.eventoId} className="flex items-center justify-between gap-3 py-2 text-sm" data-control-faltante>
                    <span className="min-w-0">
                      {nombre(f.usuarioId)} <span className="text-slate-500">· {cuando(f.cuando)}</span>
                      {f.explicado > 0 && (
                        <span className="block text-xs text-slate-500">{t('explicar.explicado', { monto: dinero(f.explicado) })}</span>
                      )}
                    </span>
                    <span className="flex shrink-0 items-center gap-3">
                      <span className="tabular text-rose-300">−{dinero(f.monto)}</span>
                      {puedeExplicar && (
                        <button
                          type="button"
                          className="btn-texto gap-1 text-xs text-slate-400"
                          onClick={() => setExplicando(f)}
                          data-control-explicar
                        >
                          <Ico icono="gastos" className="size-3.5" />
                          {t('explicar.boton')}
                        </button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {control.personas.map((p) => {
            const u = persona(p.usuarioId);
            return (
              <section key={p.usuarioId} className="tarjeta">
                {/*
                  The name opens what the card adds up (the owner, 2026-10-23: «click the name of the user
                  and see a list of their till openings/closings and sales»). The card lights only while
                  the mouse is on the name: the figures under it open nothing.
                */}
                <Link
                  to="/control/$usuarioId"
                  params={{ usuarioId: p.usuarioId }}
                  search={busqueda}
                  className="tarjeta-abrir -mx-2 -mt-2 mb-1 flex items-center gap-3 rounded-xl px-2 py-2"
                  data-control-persona={p.usuarioId}
                >
                  <header className="min-w-0 flex-1">
                    <h2 className="font-semibold">{nombre(p.usuarioId)}</h2>
                    {u && <p className="text-xs text-slate-500">{etiquetaRol(u.rol as Role)}</p>}
                  </header>
                  <Ico icono="adelante" className="size-5 shrink-0 text-slate-500" />
                </Link>
                <CifrasDePersona fila={p} rol={u?.rol} dinero={dinero} />
              </section>
            );
          })}

          {control.entradasDueno.length > 0 && (
            <section className="tarjeta">
              <header className="mb-2 flex items-center gap-3">
                <Chip icono="entrar" />
                <div className="min-w-0 flex-1">
                  <h2 className="font-semibold">{t('control.entradasDueno')}</h2>
                  <p className="text-xs text-slate-500">{t('control.entradasDuenoAyuda')}</p>
                </div>
              </header>
              <ul className="flex flex-col divide-y divide-slate-800">
                {control.entradasDueno.map((x) => (
                  <li key={`${x.dispositivoId} ${x.usuarioId}`} className="py-2 text-sm">
                    <p className="flex justify-between gap-3">
                      <span className="min-w-0 truncate font-medium">{equipo(x.dispositivoId)}</span>
                      {variosDuenos && <span className="shrink-0 text-slate-400">{nombre(x.usuarioId)}</span>}
                    </p>
                    <p className="text-xs text-slate-500">
                      {t('control.vecesUltima', { n: x.n, cuando: cuandoConHora(x.ultima) })}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
      {explicando && (
        <ExplicarFaltante eventoId={explicando.eventoId} nombre={nombre(explicando.usuarioId)} onCerrar={() => setExplicando(null)} />
      )}
    </Pagina>
  );
}

/** «Últimos 7 días» or «Últimos 30 días»: on «Control», and on a person's page from it. */
export function ElegirDias({ dias, onElegir }: { dias: DiasControl; onElegir: (d: DiasControl) => void }) {
  const t = useT();
  return (
    <div role="radiogroup" aria-label={t('control.periodo')} className="mb-4 flex gap-1 rounded-xl bg-slate-800 p-1">
      {DIAS_CONTROL.map((d) => (
        <button
          key={d}
          type="button"
          role="radio"
          aria-checked={dias === d}
          onClick={() => onElegir(d)}
          data-sin-icono="pestana"
          className={`min-h-11 flex-1 rounded-lg text-sm font-medium transition ${
            dias === d ? 'bg-marca-600 text-white' : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
          }`}
        >
          {t('control.ultimosDias', { n: d })}
        </button>
      ))}
    </div>
  );
}

/** What a person's card adds up: on «Control», and over their own page. */
export function CifrasDePersona({ fila: p, rol, dinero }: { fila: FilaPersona; rol: string | undefined; dinero: (c: number) => string }) {
  const t = useT();
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2" data-control-cifras>
      <Fila etiqueta={t('control.ventas')} n={p.ventas.n} monto={dinero(p.ventas.total)} />
      <Fila etiqueta={t('control.anuladas')} n={p.anuladas.n} monto={dinero(p.anuladas.total)} />
      <Fila etiqueta={t('control.devueltas')} n={p.devueltas.n} monto={dinero(p.devueltas.total)} />
      <Fila etiqueta={t('control.rebajas')} n={p.rebajas.n} monto={dinero(p.rebajas.total)} />
      <Fila etiqueta={t('control.rapidas')} n={p.rapidas.n} monto={dinero(p.rapidas.total)} />
      <Fila etiqueta={t('control.mermas')} n={p.mermas.n} monto={dinero(p.mermas.costo)} />
      <div className="flex items-baseline justify-between gap-3">
        <dt className="text-slate-400">{t('control.caja')}</dt>
        <dd className={`tabular ${p.caja.faltante > 0 ? 'text-rose-300' : p.caja.sobrante > 0 ? 'text-amber-200' : 'text-slate-300'}`}>
          {p.caja.faltante === 0 && p.caja.sobrante === 0
            ? t('control.cajaCuadra')
            : [p.caja.faltante > 0 && `−${dinero(p.caja.faltante)}`, p.caja.sobrante > 0 && `+${dinero(p.caja.sobrante)}`]
                .filter(Boolean)
                .join(' · ')}
        </dd>
      </div>
      <div className="flex items-baseline justify-between gap-3">
        <dt className="text-slate-400">{t('control.pinesMal')}</dt>
        <dd className={`tabular ${p.pinesMal >= 3 ? 'text-rose-300' : 'text-slate-300'}`}>{p.pinesMal}</dd>
      </div>
      {/* An owner needs nobody's yes: the row is for everybody else. */}
      {rol !== 'dueno' && <Fila etiqueta={t('control.conSiDelDueno')} n={p.conSiDelDueno.n} monto={dinero(p.conSiDelDueno.total)} />}
    </dl>
  );
}

function Fila({ etiqueta, n, monto }: { etiqueta: string; n: number; monto: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-slate-400">{etiqueta}</dt>
      {/* The figure stays on one line; a long label wraps instead («Con el sí del dueño» at 320 px). */}
      <dd className={`tabular shrink-0 whitespace-nowrap text-right ${n === 0 ? 'text-slate-500' : ''}`}>
        {n}
        {n > 0 && <span className="ml-2 text-slate-400">{monto}</span>}
      </dd>
    </div>
  );
}
